import React from "react";
import { Clock, Users, ChefHat, ChevronDown, ChevronUp, ShoppingCart, Loader2, UtensilsCrossed, Flame, Heart, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";

const getDifficultyColor = (diff) => {
  if (diff === "Easy") return "bg-green-100 text-green-700";
  if (diff === "Medium") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

const RecipeCard = ({
  recipe,
  recipeKey,
  source,
  isExpanded,
  isLoading,
  favorited,
  spoonDetails,
  lists,
  addListId,
  onExpand,
  onToggleFavorite,
  onSetAddListId,
  onAddMissing,
}) => {
  const details = (source === "spoonacular" && recipe.id) ? spoonDetails?.[recipe.id] : null;
  const mergedRecipe = details ? { ...recipe, ...details } : recipe;

  const hasIngredients = mergedRecipe.ingredients && mergedRecipe.ingredients.length > 0;
  const hasInstructions = mergedRecipe.instructions && mergedRecipe.instructions.length > 0;
  const hasMissing = hasIngredients && mergedRecipe.ingredients.some(ing => !ing.have);

  return (
    <Card className="bg-white hover:shadow-md transition-shadow overflow-hidden" data-testid={recipeKey}>
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-4 cursor-pointer" onClick={() => onExpand(recipeKey, recipe, source)}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {source === "spoonacular" && recipe.image && (
                  <img src={recipe.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                )}
                <div>
                  <h3 className="font-bold text-gray-900 leading-tight">{mergedRecipe.name}</h3>
                  {mergedRecipe.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{mergedRecipe.description}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(mergedRecipe, source); }}
                className={`p-1.5 rounded-full transition-colors ${
                  favorited ? "text-red-500 bg-red-50" : "text-gray-300 hover:text-red-400 hover:bg-red-50"
                }`}
                data-testid={`fav-${recipeKey}`}
              >
                <Heart className={`h-4 w-4 ${favorited ? "fill-current" : ""}`} />
              </button>
              {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
            </div>
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-3 flex-wrap">
            {mergedRecipe.prep_time && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <Clock className="h-3 w-3" />{mergedRecipe.prep_time} prep
              </span>
            )}
            {mergedRecipe.cook_time && mergedRecipe.cook_time !== "? min" && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <Flame className="h-3 w-3" />{mergedRecipe.cook_time}
              </span>
            )}
            {mergedRecipe.servings > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <Users className="h-3 w-3" />{mergedRecipe.servings}
              </span>
            )}
            {mergedRecipe.difficulty && (
              <Badge className={`text-xs ${getDifficultyColor(mergedRecipe.difficulty)}`}>{mergedRecipe.difficulty}</Badge>
            )}
            {mergedRecipe.category && <Badge variant="outline" className="text-xs capitalize">{mergedRecipe.category}</Badge>}
            {source === "spoonacular" && (
              <Badge className="text-xs bg-orange-100 text-orange-700">Tasty Picks</Badge>
            )}
          </div>

          {/* Ingredient preview pills */}
          {hasIngredients && (
            <div className="mt-3 flex flex-wrap gap-1">
              {mergedRecipe.ingredients.slice(0, 5).map((ing, i) => (
                <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${
                  ing.have ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                }`}>
                  {ing.name}
                </span>
              ))}
              {mergedRecipe.ingredients.length > 5 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  +{mergedRecipe.ingredients.length - 5} more
                </span>
              )}
            </div>
          )}

          {/* Spoonacular: show used/missing ingredient names when not yet expanded with details */}
          {source === "spoonacular" && !hasIngredients && recipe.used_ingredients && (
            <div className="mt-3 flex flex-wrap gap-1">
              {recipe.used_ingredients.slice(0, 4).map((name, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{name}</span>
              ))}
              {recipe.missing_ingredients && recipe.missing_ingredients.slice(0, 3).map((name, i) => (
                <span key={`m-${i}`} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{name}</span>
              ))}
            </div>
          )}
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t px-4 pb-4 pt-3 bg-gray-50/50 space-y-4">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                <span className="text-sm text-gray-500">Loading recipe details...</span>
              </div>
            )}

            {!isLoading && hasIngredients && (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  <UtensilsCrossed className="h-3.5 w-3.5 inline mr-1" />
                  Ingredients
                </h4>
                <div className="space-y-1">
                  {mergedRecipe.ingredients.map((ing, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        ing.have ? "bg-green-500" : "bg-gray-300"
                      }`} />
                      <span className="text-gray-500 min-w-[100px] text-xs">{ing.amount}</span>
                      <span className={ing.have ? "text-gray-900" : "text-gray-500"}>{ing.name}</span>
                      {!ing.have && (
                        <Badge variant="outline" className="text-xs ml-auto text-gray-400">need</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isLoading && hasInstructions && (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Instructions</h4>
                <ol className="space-y-2">
                  {mergedRecipe.instructions.map((step, i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {mergedRecipe.tips && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">Chef's Tip:</span> {mergedRecipe.tips}
                </p>
              </div>
            )}

            {mergedRecipe.source_url && (
              <a
                href={mergedRecipe.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                <ExternalLink className="h-3 w-3" /> View original recipe
              </a>
            )}

            {!isLoading && hasMissing && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={addListId || ''}
                    onChange={(e) => onSetAddListId(Number(e.target.value))}
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white"
                    data-testid={`list-select-${recipeKey}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {lists.map((list) => (
                      <option key={list.id} value={list.id}>{list.name}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs whitespace-nowrap"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddMissing(mergedRecipe.ingredients, addListId);
                    }}
                    data-testid={`add-missing-${recipeKey}`}
                  >
                    <ShoppingCart className="h-3.5 w-3.5 mr-1" />
                    Add Missing
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecipeCard;
